<template>
  <main class="bg-white">
    <v-toolbar flat class="main-header main-header__shadow mb-6 px-5 py-2">
      <v-toolbar-title class="main-header__title">
        <h1>
          <v-icon class="mx-3 mb-1" @click="back">mdi-arrow-left</v-icon>
          <span v-if="!isEdited">{{ $t("new_chapter") }}</span>
          <span v-else>{{ $t("edit_chapter") }}</span>
        </h1>
      </v-toolbar-title>
    </v-toolbar>
    <v-container>
      <v-alert v-if="globalServerErrors !== null" dense outlined type="error">
        <p
          class="mb-0"
          v-for="globalErr in globalServerErrors"
          :key="globalErr"
        >
          {{ globalErr[0] }}
        </p>
      </v-alert>
      <formWrapper :validator="$v.form">
        <form @submit.prevent="handleSave" class="form-box">
          <v-row>
            <v-col cols="12" v-if="isEdited">
              <locale-select @change="fireLocaleChange" />
            </v-col>
            <v-col cols="12">
              <section class="transparent-field mt-5">
                <v-switch
                  v-model="form.visible"
                  inset
                  :false-value="0"
                  :true-value="1"
                  :label="$t('visible')"
                  color="success"
                ></v-switch>
              </section>
            </v-col>

            <v-col class="py-0" cols="12" lg="4">
              <form-group name="name" :attribute="`fields.name`">
                <template slot-scope="{ attrs, listeners }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model.trim="form.name"
                    type="text"
                    dense
                  >
                  </v-text-field>
                </template>
              </form-group>
            </v-col>
            <v-col class="py-0" cols="12" lg="4">
              <form-group
                name="exam_questions_count"
                :attribute="`fields.exam_questions_count`"
              >
                <template slot-scope="{ attrs, listeners }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model.trim="form.exam_questions_count"
                    type="number"
                    dense
                  >
                  </v-text-field>
                </template>
              </form-group>
            </v-col>
            <v-col class="py-0" cols="12" lg="4">
              <form-group
                name="exam_duration_in_minutes"
                :attribute="`fields.exam_duration_in_minutes`"
              >
                <template slot-scope="{ attrs, listeners }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model.trim="form.exam_duration_in_minutes"
                    type="number"
                    dense
                  >
                  </v-text-field>
                </template>
              </form-group>
            </v-col>
            <v-col class="py-0" cols="12">
              <form-group name="description" :attribute="`fields.description`">
                <template slot-scope="{ attrs, listeners }">
                  <v-textarea
                    v-bind="attrs"
                    v-on="listeners"
                    no-resize
                    rows="4"
                    outlined
                    v-model.trim="form.description"
                    :label="$t('fields.description')"
                    type="text"
                  >
                  </v-textarea>
                </template>
              </form-group>
            </v-col>
            <v-col class="py-0" cols="12">
              <v-row>
                <v-col cols="12" lg="6">
                  <v-row>
                    <v-col cols="6">
                      <form-group name="mediaVideo.url">
                        <template slot-scope="{ attrs, listeners }">
                          <v-text-field
                            :label="$t('fields.video_url')"
                            v-bind="attrs"
                            v-on="listeners"
                            outlined
                            v-model="form.mediaVideo.url"
                            type="text"
                            dense
                          >
                          </v-text-field>
                        </template>
                      </form-group>
                    </v-col>
                    <v-col cols="4">
                      <form-group
                        name="mediaVideo.name"
                        attribute="fields.video_name"
                      >
                        <template slot-scope="{ attrs, listeners }">
                          <v-text-field
                            :label="$t('fields.video_name')"
                            v-bind="attrs"
                            v-on="listeners"
                            outlined
                            v-model="form.mediaVideo.name"
                            type="text"
                            dense
                          >
                          </v-text-field>
                        </template>
                      </form-group>
                    </v-col>
                    <v-col cols="2" class="d-flex justify-center">
                      <v-btn
                        @click.prevent="addMediaVideos"
                        class="primary mx-2"
                        :disabled="
                          form.mediaVideo.url === '' ||
                          form.mediaVideo.name === '' ||
                          $v.form.mediaVideo.url.$invalid ||
                          $v.form.mediaVideo.name.$invalid
                        "
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>

                  <v-row
                    v-for="(media, index) in form.media_videos"
                    :key="index"
                  >
                    <v-col cols="6">
                      <v-text-field
                        outlined
                        v-model="media.url"
                        type="text"
                        dense
                        readonly
                      >
                      </v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        :label="$t('fields.video_name')"
                        outlined
                        v-model="media.name"
                        type="text"
                        dense
                        readonly
                      >
                      </v-text-field>
                    </v-col>
                    <v-col cols="2" class="d-flex justify-center">
                      <v-btn
                        @click.prevent="deleteMediaVideos(media, index)"
                        class="error"
                      >
                        <v-icon>mdi-minus</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
                <v-divider vertical></v-divider>
                <v-col cols="12" lg="6">
                  <v-row>
                    <v-col cols="6">
                      <fileUpload
                        @fileSelected="onFileSelected"
                        :locale="locale"
                        class="file-upload__image"
                        :maxSize="maxSize"
                        :reset="reset"
                      />
                    </v-col>
                    <v-col cols="4">
                      <form-group
                        name="mediaFile.name"
                        attribute="fields.file_name"
                      >
                        <template slot-scope="{ attrs, listeners }">
                          <v-text-field
                            height="50"
                            :label="$t('fields.file_name')"
                            v-bind="attrs"
                            v-on="listeners"
                            outlined
                            v-model="form.mediaFile.name"
                            type="text"
                            dense
                          >
                          </v-text-field>
                        </template>
                      </form-group>
                    </v-col>
                    <v-col cols="2" class="d-flex justify-center">
                      <v-btn
                        @click.prevent="addMediaFiles"
                        class="primary mx-1"
                        :disabled="
                          form.mediaFile.file === '' ||
                          form.mediaFile.name === '' ||
                          $v.form.mediaFile.name.$invalid
                        "
                      >
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row
                    v-for="(mediaFile, index) in form.media_files"
                    :key="index"
                  >
                    <v-col cols="6" class="text-center align-self-center">
                      <v-text-field
                        v-if="mediaFile.file"
                        height="50"
                        outlined
                        v-model="mediaFile.file.name"
                        type="text"
                        readonly
                        dense
                      >
                      </v-text-field>
                      <a v-else :href="mediaFile.url" target="_blank">
                        <img
                          width="40px"
                          height="40px"
                          :src="require('@/assets/imgs/pdf.png')"
                          alt="pdf file"
                        />
                      </a>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        height="50"
                        :label="$t('fields.file_name')"
                        outlined
                        v-model="mediaFile.name"
                        type="text"
                        readonly
                        dense
                      >
                      </v-text-field>
                    </v-col>
                    <v-col cols="2" class="d-flex justify-center">
                      <v-btn
                        @click.prevent="deleteMediaFile(mediaFile, index)"
                        class="error"
                      >
                        <v-icon>mdi-minus</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12">
              <v-btn
                class="success text-capitalize"
                large
                type="submit"
                width="30%"
                :disabled="$v.form.$invalid || validMedia"
                :loading="loading"
                ><span>{{ $t("button.save") }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </form>
      </formWrapper>
    </v-container>
  </main>
</template>

<script>
import {
  required,
  minLength,
  maxLength,
  url,
  maxValue,
  minValue,
} from "vuelidate/lib/validators";
import cookie from "js-cookie";

import {
  StoreData,
  ShowData,
  UpdateData,
  DeleteMedia,
} from "@/helpers/apiMethods";

export default {
  name: "ChaptersCrud",

  components: {
    DateTimePicker: () => import("@/components/DateTimePicker"),
    FileUpload: () => import("@/components/fileUpload"),
  },
  computed: {
    validMedia() {
      let valid = false;
      const { mediaFile, mediaVideo } = this.form;
      if (
        mediaFile.name !== "" ||
        mediaFile.file !== "" ||
        mediaVideo.name !== "" ||
        mediaVideo.url !== ""
      ) {
        valid = true;
      } else {
        valid = false;
      }
      return valid;
    },
  },
  data() {
    return {
      globalServerErrors: null,
      isEdited: false,
      loadingData: false,
      loading: false,
      locale: cookie.get("language") || "en",
      reset: false,

      maxSize: 5.12,
      tags: [],

      mediaVideoCounter: 0,
      mediaFileCounter: 0,

      form: {
        updatedMediaVideos: [],
        updatedMediaFiles: [],
        name: "",
        visible: 1,
        description: "",
        exam_questions_count: "",
        exam_duration_in_minutes: "",
        media_videos: [],
        media_files: [],
        mediaFile: {
          name: "",
          file: "",
        },
        mediaVideo: {
          name: "",
          url: "",
        },
      },
    };
  },

  created() {
    this.editCreateBoolean();
  },
  methods: {
    addMediaFiles() {
      this.form.media_files.push({ ...this.form.mediaFile });
      this.form.updatedMediaFiles.push({ ...this.form.mediaFile });

      this.resetData("mediaFile");
    },
    handleDeleteMedia(item, fieldName, index) {
      this.popUp().then((value) => {
        if (!value.dismiss) {
          DeleteMedia(item.id).then(() => {
            this.$delete(this.form[fieldName], index);
          });
        }
      });
    },
    deleteMediaFile(mediaFile, index) {
      if (!mediaFile.id) {
        this.$delete(this.form.media_files, index);
      } else {
        this.handleDeleteMedia(mediaFile, "media_files", index);
      }
    },
    onFileSelected({ file }) {
      this.form.mediaFile.file = file;
    },
    resetData(data) {
      for (const key in this.form[data]) {
        this.form[data][key] = "";
      }
      this.reset = true;
      setTimeout(() => {
        this.reset = false;
      }, 50);
    },
    addMediaVideos() {
      this.form.media_videos.push({ ...this.form.mediaVideo });
      this.form.updatedMediaVideos.push({ ...this.form.mediaVideo });

      this.resetData("mediaVideo");
    },
    deleteMediaVideos(media, index) {
      if (!media.id) {
        this.$delete(this.form.media_videos, index);
      } else {
        this.handleDeleteMedia(media, "media_videos", index);
      }
    },

    deleteUrl(index) {
      this.$delete(this.form.promo_media, index);
    },

    editCreateBoolean() {
      if (this.$route.params.id) {
        this.isEdited = true;
      } else {
        this.isEdited = false;
      }
    },

    show(id, locale) {
      ShowData({
        reqName: `admin-panel/chapters`,
        id: id,
        locale,
      })
        .then((res) => {
          const { chapter } = res.data;
          this.form = {
            ...this.form,
            name: chapter.name,
            exam_duration_in_minutes: chapter.exam_duration_in_minutes,
            exam_questions_count: chapter.exam_questions_count,
            description: chapter.description,

            media_videos: chapter.media_videos.map((media) => {
              return {
                url: media.path,
                id: media.id,
                name: media.title,
              };
            }),
            media_files: chapter.media_files.map((media) => {
              return {
                url: media.path,
                id: media.id,
                name: media.title,
              };
            }),
          };
          if (chapter.visible == true) {
            this.form.visible = "1";
          } else {
            this.form.visible = "0";
          }
        })
        .catch(() => {});
    },
    update(formData) {
      this.loading = true;

      UpdateData({
        reqName: `admin-panel/chapters`,
        data: formData,
        id: this.$route.params.id,
      })
        .then(() => {
          this.isEdited = false;
          this.loading = false;
          this.back();
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          this.scrollTop();
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildMediaVideos(fieldName, formData) {
      this.form[fieldName].forEach((el, index) => {
        if (el.name !== "" && el.url !== "") {
          formData.append(`media_videos[${index}][url]`, el.url);
          formData.append(`media_videos[${index}][name]`, el.name);
        }
      });

      return formData;
    },
    buildMediaFiles(fieldName, formData) {
      this.form[fieldName].forEach((el, index) => {
        if (el.name !== "" && el.file !== "") {
          formData.append(`media_files[${index}][file]`, el.file);
          formData.append(`media_files[${index}][name]`, el.name);
        }
      });

      return formData;
    },
    buildData() {
      if (this.isEdited) {
        delete this.form.image;
      }

      const explodeField = ["media_videos", "media_files"];
      const formData = new FormData();
      for (const key in this.form) {
        if (!explodeField.includes(key) && this.form[key] !== "") {
          formData.append(key, this.form[key]);
        }
      }
      if (!this.isEdited) {
        this.buildMediaVideos("media_videos", formData);
        this.buildMediaFiles("media_files", formData);
      } else {
        this.buildMediaVideos("updatedMediaVideos", formData);
        this.buildMediaFiles("updatedMediaFiles", formData);
      }
      formData.append("locale", this.locale);
      formData.append("subject", this.$route.query.subject);

      if (this.isEdited) {
        formData.append("_method", "put");
      }
      return formData;
    },
    handleSave() {
      if (!this.isEdited) {
        this.create(this.buildData());
      } else {
        this.update(this.buildData());
      }
    },

    create(formData) {
      this.loading = true;

      StoreData({
        reqName: `admin-panel/chapters`,
        data: formData,
      })
        .then(() => {
          this.loading = false;

          this.back();
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          this.scrollTop();
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    back() {
      history.go(-1);
    },
    scrollTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    fireLocaleChange(locale) {
      this.show(this.$route.params.id, locale);
      this.locale = locale;
    },
  },
  watch: {
    $route: {
      handler({ params }) {
        if (params.id) {
          this.show(params.id);
        }
      },
      immediate: true,
    },
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(190),
      },
      exam_questions_count: {
        required,
        minValue: minValue(1),
        maxValue: maxValue(5000),
      },
      exam_duration_in_minutes: {
        required,
        minValue: minValue(1),
        maxValue: maxValue(5000),
      },

      description: {
        minLength: minLength(3),
        maxLength: maxLength(10000),
      },
      mediaVideo: {
        name: {
          minLength: minLength(2),
          maxLength: maxLength(190),
        },
        url: {
          url,
        },
      },
      mediaFile: {
        name: {
          minLength: minLength(2),
          maxLength: maxLength(190),
        },
      },
    },
  },
};
</script>
